import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSpinner, faUsers, faClipboardList, faShoppingCart, faGraduationCap } from '@fortawesome/free-solid-svg-icons';
import { Line } from '@ant-design/plots';
import { dashboardAdminService } from '../../../services/dashboardAdminService';
import Sparkline from './Sparkline';

/**
 * Modal de drill-down de uma extensão. Aberto ao clicar em uma linha
 * da tabela de ranking. Mostra: dados básicos, resumo numérico, top
 * cursos, sparkline 12m e listas curtas de atividade recente.
 */

const fmtMoeda = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);
const fmtNumero = (v) => new Intl.NumberFormat('pt-BR').format(v || 0);
const fmtData = (v) => v ? new Date(v).toLocaleDateString('pt-BR') : '∅';

const STATUS_PEDIDO_COLOR = {
  Concluido: 'bg-green-100 text-green-700',
  Pendente:  'bg-yellow-100 text-yellow-700',
  Cancelado: 'bg-red-100 text-red-700',
  Rascunho:  'bg-gray-100 text-gray-700',
};
const STATUS_MATR_COLOR = {
  Ativa:     'bg-green-100 text-green-700',
  Concluida: 'bg-blue-100 text-blue-700',
  Trancada:  'bg-yellow-100 text-yellow-700',
  Cancelada: 'bg-red-100 text-red-700',
};

const ModalDetalhesExtensao = ({ extensaoId, periodo, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);

  useEffect(() => {
    if (!extensaoId) return;
    const ac = new AbortController();
    (async () => {
      try {
        setLoading(true);
        setErro(null);
        const resp = await dashboardAdminService.extensaoDetalhes(extensaoId, { periodo, signal: ac.signal });
        setData(resp.data);
      } catch (e) {
        if (e.name !== 'CanceledError') setErro(e.response?.data?.message || 'Erro ao carregar detalhes');
      } finally {
        setLoading(false);
      }
    })();
    return () => ac.abort();
  }, [extensaoId, periodo]);

  useEffect(() => {
    const onEsc = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [onClose]);

  if (!extensaoId) return null;

  const sparkValues = data?.sparkline_matriculas_12m?.map(s => s.qtd) || [];

  // Gráfico de evolução em modal (Line padrão)
  const lineConfig = {
    data: data?.sparkline_matriculas_12m || [],
    xField: 'mes',
    yField: 'qtd',
    point: { shapeField: 'circle', sizeField: 3 },
    smooth: true,
    height: 200,
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-2xl w-full max-w-4xl my-8"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Cabeçalho */}
        <div className="px-6 py-4 border-b flex items-center justify-between sticky top-0 bg-white rounded-t-lg">
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              {data?.extensao?.nome || 'Carregando…'}
            </h2>
            {data?.extensao && (
              <p className="text-xs text-gray-500 mt-0.5">
                Extensão #{data.extensao.id} • Status: {data.extensao.status || '∅'}
              </p>
            )}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1" title="Fechar (Esc)">
            <FontAwesomeIcon icon={faTimes} className="text-xl" />
          </button>
        </div>

        <div className="p-6">
          {loading && (
            <div className="text-center text-gray-400 py-12">
              <FontAwesomeIcon icon={faSpinner} className="animate-spin text-3xl mb-2" />
              <p>Carregando detalhes…</p>
            </div>
          )}
          {erro && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4 text-red-700 text-sm">{erro}</div>
          )}

          {!loading && data && (
            <div className="space-y-6">
              {/* KPIs da extensão */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-blue-50 rounded-md p-3">
                  <p className="text-xs text-blue-700 uppercase">Alunos</p>
                  <p className="text-2xl font-bold text-gray-900">{fmtNumero(data.alunos.total)}</p>
                  <p className="text-xs text-gray-500">{fmtNumero(data.alunos.ativos)} ativos</p>
                </div>
                <div className="bg-indigo-50 rounded-md p-3">
                  <p className="text-xs text-indigo-700 uppercase">Matr. (período)</p>
                  <p className="text-2xl font-bold text-gray-900">{fmtNumero(data.pedidos.qtd_periodo)}</p>
                </div>
                <div className="bg-purple-50 rounded-md p-3">
                  <p className="text-xs text-purple-700 uppercase">Pedidos total</p>
                  <p className="text-2xl font-bold text-gray-900">{fmtNumero(data.pedidos.total)}</p>
                  <p className="text-xs text-gray-500">
                    {fmtNumero(data.pedidos.pendentes)} pend / {fmtNumero(data.pedidos.concluidos)} concl
                  </p>
                </div>
                <div className="bg-green-50 rounded-md p-3">
                  <p className="text-xs text-green-700 uppercase">Receita (período)</p>
                  <p className="text-2xl font-bold text-gray-900">{fmtMoeda(data.pedidos.valor_periodo)}</p>
                </div>
              </div>

              {/* Sparkline / evolução */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold text-gray-700">Evolução de matrículas (12 meses)</h3>
                  <Sparkline data={sparkValues} width={120} height={28} color="#4f46e5" />
                </div>
                <div className="bg-gray-50 rounded-md p-3">
                  <Line {...lineConfig} />
                </div>
              </div>

              {/* Top cursos */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-2">
                  <FontAwesomeIcon icon={faGraduationCap} className="text-indigo-600 mr-1" />
                  Top cursos ativos
                </h3>
                {data.top_cursos.length > 0 ? (
                  <ul className="divide-y border rounded-md">
                    {data.top_cursos.map(c => (
                      <li key={c.id} className="px-3 py-2 flex justify-between items-center text-sm">
                        <span className="text-gray-900">{c.nome}</span>
                        <span className="text-gray-600">
                          <span className="font-medium text-green-700">{fmtNumero(c.ativas)}</span>
                          <span className="text-gray-400"> / {fmtNumero(c.total)} total</span>
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-gray-400 text-sm">Nenhum curso com matrículas ativas</p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Matrículas recentes */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">
                    <FontAwesomeIcon icon={faUsers} className="text-indigo-600 mr-1" />
                    Matrículas recentes
                  </h3>
                  {data.matriculas_recentes.length > 0 ? (
                    <ul className="divide-y border rounded-md">
                      {data.matriculas_recentes.map(m => (
                        <li key={m.id} className="px-3 py-2 text-xs">
                          <div className="flex justify-between gap-2">
                            <span className="font-medium text-gray-900 truncate">{m.aluno_nome}</span>
                            <span className={`px-2 py-0.5 rounded-full text-[10px] ${STATUS_MATR_COLOR[m.status] || 'bg-gray-100 text-gray-700'}`}>{m.status}</span>
                          </div>
                          <p className="text-gray-500 truncate">{m.curso_nome} • {fmtData(m.dataMatricula)}</p>
                        </li>
                      ))}
                    </ul>
                  ) : <p className="text-gray-400 text-sm">∅</p>}
                </div>

                {/* Pedidos recentes */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">
                    <FontAwesomeIcon icon={faShoppingCart} className="text-purple-600 mr-1" />
                    Pedidos recentes
                  </h3>
                  {data.pedidos_recentes.length > 0 ? (
                    <ul className="divide-y border rounded-md">
                      {data.pedidos_recentes.map(p => (
                        <li key={p.id} className="px-3 py-2 text-xs">
                          <div className="flex justify-between gap-2">
                            <span className="font-medium text-gray-900 truncate">{p.pessoa_nome || 'Sem nome'}</span>
                            <span className={`px-2 py-0.5 rounded-full text-[10px] ${STATUS_PEDIDO_COLOR[p.status] || 'bg-gray-100 text-gray-700'}`}>{p.status}</span>
                          </div>
                          <p className="text-gray-500 truncate">{fmtMoeda(p.valor)} • {fmtData(p.cadastradoEm)}</p>
                        </li>
                      ))}
                    </ul>
                  ) : <p className="text-gray-400 text-sm">∅</p>}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ModalDetalhesExtensao;
